<template>
  <div class="chapterOne">
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">第3章 真实世界的AI（8 贝叶斯规则）</div>
      <div style="marginTop:30px;fontSize:12px;color:rgb(153,153,153);">2020-3-14 20:12:32</div>
      <div class="text fontBig">现代AI方法之所以能在现实世界中实际发挥作用，主要是因为它处理不确定性因素的能力较强，而不象1960-1980年代早期的大多数老式方法那样，对不确定性问题显得无能为力。</div>
      <div class="text fontBig">在第 3 章中，我们将介绍以下三部分内容。</div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;" @click="jumpOne">一、<span style="color:#0090ff;textDecoration:underline;">概率和优势比</span>（0/2）</div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;"  @click="jumpTow">二、<span style="color:#0090ff;textDecoration:underline;">贝叶斯规则 </span>（0/2）</div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;"  @click="jumpThree">三、<span style="color:#0090ff;textDecoration:underline;">朴素贝叶斯分类 </span>（0/2）</div>
      <div  id="productId"><div style="marginTop:50px;fontSize:22px;fontWeight:bold;">第2节 贝叶斯规则</div></div>
      <div class="text fontBig">我们不会深入探讨概率演算的细节及其在各种AI应用程序中的使用方式，但是我们将讨论一个非常重要的公式。</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">一、贝叶斯公式</div>
      <div class="text fontBig">我们将执行此操作，因为此特定公式既简单又优雅，而且功能强大。它可以用来衡量医学，法院和许多（如果不是全部）科学学科中相互矛盾的证据。该公式称为贝叶斯规则（或贝叶斯公式）。</div>
      <div class="text fontBig">我们将通过一个简单的医学诊断问题来展示贝叶斯规则的威力，在这个问题中，我们的直觉很难将相互矛盾的证据结合起来。然后，我们将展示如何使用贝叶斯规则来构建能够处理冲突和噪声观测的人工智能方法。</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">1. 事前和事后优势比</div>
      <div class="text fontBig">贝叶斯规则可以以多种形式表达。最简单的是优势比。我们的想法是要对发生的事情（针对未发生的事情）进行优势比计算，我们将其写为事前优势比。“事前”一词是指我们在获得一些可能相关的新信息之前对优势比的评估。该公式的目的是在有新信息可用时更新事前优势比，以获取事后优势比或获取信息后的优势比（事后的字典含义是“后来发生的事”）。</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">2. 优势比如何变化</div>
      <div class="text fontBig">为了权衡新信息，并确定获得优势比的可能性如何变化，我们需要考虑在替代情况下遇到这些信息的可能性。以今天晚些时候下雨的优势比为例，想象一下在广州早上起床的情景，365天中下雨的机会为156日。因此，不下雨的天数是209，转换为先前的下雨优势比为156:209。</div>
      <img style="width:400px;marginLeft:130px" src="../assets/08-01.png" alt="">
      <div class="text fontBig">然而，睁开眼睛看向外面后，您会发现它是阴天。假设在阴雨天里有乌云的早晨的可能性是十分之九——这意味着十分之一的雨天是从蓝天开始的。但是有时也有无雨的云：在无雨天有云的机会是十分之一。与无雨天相比，在雨天有云的机会要高多少？请仔细考虑这一点，因为理解以下问题并获得答案很重要。</div>
      <div class="text fontBig">答案是，在下雨天有乌云的优势比是不下雨天的9倍：在下雨天有乌云的机会是10中有9，而在不下雨天出现乌云的机会是10中之1，因而高为九倍。</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">3. 似然比</div>
      <div class="text fontBig">上述比率（在下雨天比无雨天有乌云的优势比高9倍）称为似然比。更一般地，似然比是在关注事件（在上面为下雨）的情况下观察的概率除以在没有事件（在上面不下雨）的情况下观察的概率。请把前一句话读几遍。它可能看起来有些令人生畏，但是如果您只是集中精力背诵它，就可能难于消化。我们会详细地带你走过台阶，只是不要失去勇气。我们就快到了。</div>
      <div class="text fontBig" style="marginTop:30px;">因此我们得出结论，在有乌云的早晨，我们有：似然比=（9/10）/（1/10）= 9</div>
      <div class="text fontBig">将事前优势比转换为事后优势比的强大贝叶斯规则如下：事后优势比=似然比×事前优势比</div>
      <div class="text fontBig">现在您可能正在思考：等等，这就是公式吗？这是令人生畏的乘法！这就是公式，我们说的很简单，不是吗？您不会想象简单的乘法可以用于各种难以置信的有用的应用程序，但是它可以。我们将研究几个示例，以证明这一点。</div>
      <div class="text fontBig" style="color:#f15a23;">【注意】</div>
      <div class="text fontBig" style="color:#f15a23;fontWeight:bold">如果您在以下练习中遇到任何麻烦，您可能需要阅读上述材料几次，并花一些时间，如果这样做没有效果，则可以在线查找更多材料。只需提一个建议：贝叶斯规则可以用多种不同形式编写，而我们使用的比率形式并不是最常见的形式。以下是一些可能会有用的链接。</div>
      <div class="text fontBig" style="color:#f15a23;">数学博士：贝叶斯定理和医学检验</div>
      <div class="text fontBig" style="color:#f15a23;">更好地解释：用比率理解贝叶斯定理</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">【知识巩固】</div>
      <div class="text fontBig"><b>练习10：贝叶斯规则（2之1）</b>（Ref/1854:159）</div>
      <div class="text fontBig">应用贝叶斯规则计算早晨在广州观测到的乌云降雨的事后优势比。</div>
      <div class="text fontBig">正如我们上面计算的，降雨的事前优势比是206：159，观察乌云的似然比是 9</div>
      <div class="text fontBig"> 以优势比xx：yy的形式给出结果，其中xx和yy是数字。（请注意，XX和YY也并不意味着数字应该各有两位数字。）请记住，乘以优势比时，你应该只乘分子（第xx部分）。例如，如果您将优势比5：3乘以5，则结果为25：3。即使双方都有一个共同的因素，也可以在不简化表达式的情况下给出答案。</div>
      <div class="text fontBig" style="textAlign:center;">回答：<input type="text" class="anwserInput" :disabled="contenteditable"></div>
      <button class="sentBtn" style="marginLeft:290px;" @click="sentBtn">提交</button>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">二、实践中的贝叶斯规则：乳腺癌筛查</div>
      <div class="text fontBig">我们的第一个实际应用是使用贝叶斯规则的经典示例，即医学诊断。此示例还说明了在处理不确定信息时的普遍偏见，即基准率谬误。</div>
      <img style="width:400px;marginLeft:130px" src="../assets/08-02.png" alt="">
      <div class="text fontBig">考虑进行乳腺钼靶筛查以检查乳腺癌。为了简化数字，我们假设百分之五的女性患有乳腺癌。假设一个人得了乳腺癌，那么乳房X光检查会发现100例中有80例是乳腺癌。当检测结果表明乳腺癌存在时，我们说结果是阳性的，尽管对于被检测者来说，当然没有什么阳性（一种技术上的说法是检测的灵敏度是80%）。</div>
      <div class="text fontBig">该测试也可能在另一个方向上失败，即在不存在乳腺癌的情况下指示乳腺癌。这称为误报。假设如果被测人实际上没有患乳腺癌，那么该测验仍然呈阳性的机会是十分之一。</div>
      <div class="text fontBig">根据上述概率，您可以计算似然比。在下一个练习中，您会发现它的用途。如果您忘记了似然比的计算方式，则不妨在本节前面的术语栏中打钩，并重新查看下雨的例子。</div>
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">【知识巩固】</div>
      <div class="text fontBig"><b>练习11：贝叶斯规则（2之2）</b>（Ref/40/95）</div>
      <div class="text fontBig">考虑上述乳腺癌的情况。一个普通的女人接受乳房X光检查，并得到阳性结果，提示乳腺癌。考虑到检查结果呈阳性，您认为她患乳腺癌的优势比是多少？</div>
      <div class="text fontBig">首先，在不应用贝叶斯规则的情况下使用直觉，然后在一张纸上写下（不在下面的答案框中），您认为在阳性检测结果之后患乳腺癌的可能性。直观的答案将不属于您的答案。仅供参考。</div>
      <div class="text fontBig">接下来，使用贝叶斯规则计算她患乳腺癌的事后优势比。这将是您的答案。</div>
      <div class="text fontBig">提示：</div>
      <div class="text fontBig">1.首先计算事前优势比。</div>
      <div class="text fontBig">2.确定事件（癌症）情况下的观察概率。</div>
      <div class="text fontBig">3.确定无事件（无癌症）的情况下观察的概率。</div>
      <div class="text fontBig">4.获得似然比作为上述两个概率的比。</div>
      <div class="text fontBig">5.最后，将事前优势比乘以似然比。</div>
      <div class="text fontBig">在下面输入事后优势比作为您的解决方案。以xx：yy的形式给出答案，其中xx和yy是数字，即使双方都有一个公因子，也无需简化表达式。</div>
      <div class="text fontBig" style="textAlign:center;">回答：<input type="text" class="anwserInput" :disabled="contenteditableOne"></div>
      <button class="sentBtn" style="marginLeft:290px;" @click="sentBtnOne">提交</button>
      <div class="backBtn" style="margin:20px auto;textAlign:center;">
        <button @click="goBlank" style="cursor: pointer;">上一页</button>
        <button @click="goMune"  style="marginLeft:15px;marginRight:15px;cursor: pointer;">返回目录页</button>
        <button @click="NextClk" style="cursor: pointer;">下一页</button>
      </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      choosesix:'',
      chooseseven:'',
      chooseeight:'',
      choosenine:'',
      isDisabledOne:false,
      contenteditable:false,
      contenteditableOne:false,
    }
  },
  methods:{
    sentBtn(){
      this.contenteditable = true;
    },
    sentBtnOne(){
      this.contenteditableOne = true;
    },
    jumpTow(){
       const returnEle = document.querySelector("#productId");  //productId是将要跳转区域的id
        if (!!returnEle) {
          returnEle.scrollIntoView(true); // true 是默认的
        }
        document.querySelector("counter1").scrollIntoView(true); //这里的counter1是将要返回地方的id
    },
    jumpOne(){
      this.$router.push('/chapterSeven');
    },
    jumpThree(){
      this.$router.push('/chapterNine');
    },
    goBlank(){
      this.$router.push('/chapterSeven');
    },
    goMune(){
      this.$router.push('/');
    },
    NextClk(){
      this.$router.push('/chapterNine');
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.chapterOne{
    width: 650px;
    margin: 0 auto;
    padding: 0px 75px;
}
.text{
    text-indent:2em;
}
.fontBig{
    font-size: 16px;
    color: rgb(102, 102, 102);
    line-height: 30px;
    
}
.sentBtn{
  width: 100px;
  margin-left: 240px;
  margin-top: 25px;
  margin-bottom: 25px;
  cursor: pointer;
}
.anwser{
  width: 200px;
  height: 200px;
  border: 1px dashed black;
  margin-left: 230px;
  overflow:auto;
  font-size: 14px;
  outline: none;
  margin-top: 10px;
  padding: 10px;
}
.anwserInput{
  border: none;
  outline: none;
  border-bottom: 1px solid;
  font-size:16px;
}
.backBtn button{
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/chapterEight.vue